// src/PO-Process/POReport.jsx

import { useState, useEffect, useRef } from "react";
import { Box, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import api from "../axiosConfig";
import Card, { CardContent } from "./components/ui/card";
import ContainedButton from "../components/ui/ContainedButton";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

const POReport = ({ open }) => {
  const [selectedDate, setSelectedDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );

  const [report1, setReport1] = useState([]);
  const [colDefs1, setColDefs1] = useState([]);
  const gridRef = useRef(null);

  const [gridApi, setGridApi] = useState(null);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const onColumnVisible = () => {
    if (gridApi) {
      gridApi.refreshClientSideRowModel("group");
      gridApi.refreshClientSideRowModel("aggregate");
    }
  };

  // -------------------------------------------------------------------
  // LOAD DATA
  // -------------------------------------------------------------------
  const loadReports = async () => {
    const res = await api.get("/po-report", {
      params: {
        date: selectedDate,
      },
    });

    const r1 = (res.data.report1 || []).map(row => ({
      ...row,
      Quantity: row.Quantity ? Number(row.Quantity) : 0,
    }));

    setReport1(r1);

    if (r1.length > 0) {
      const orderedCols = [
        "DC",
        "Channel",
        "Pricelist",
        "Product_Code",
        "Product_Name",
        "Quantity",
        "Outlet_Code",
        "Beat",
        "Van",
        "Delivery_Date",
      ];

      const dynamicColDefs = orderedCols.map((key) => {
        if (key === "Quantity") {
          return {
            headerName: "Quantity",
            field: "Quantity",
            sortable: true,
            filter: true,
            resizable: true,
            enableValue: true,
            aggFunc: "sum",
            editable: false,
            enableRowGroup: false,
            enablePivot: false,
            cellStyle: params => {
              if (params.node.group || params.node.footer) {
                return { fontWeight: "bold" };
              }
              return null;
            }
          };
        }

        if (key === "Delivery_Date") {
          return {
            headerName: "Delivery Date",
            field: "Delivery_Date",
            sortable: true,
            filter: true,
            resizable: true,
            enableRowGroup: true,
            valueFormatter: (params) => {
              if (!params.value) return "";
              // Ensure formatting as YYYY-MM-DD only
              return dayjs(params.value).format("YYYY-MM-DD");
            },
          };
        }

        // All other columns except Quantity are groupable
        return {
          headerName: key,
          field: key,
          sortable: true,
          filter: true,
          resizable: true,
          enableRowGroup: key !== "Quantity",
          enablePivot: key !== "Quantity",
        };
      });

      setColDefs1(dynamicColDefs);
    }
  };

  useEffect(() => {
    if (gridApi) {
      gridApi.refreshClientSideRowModel('aggregate');
    }
  }, [colDefs1, report1, gridApi]);

  // -------------------------------------------------------------------
  // FULL AG GRID SETTINGS FOR PIVOT TABLE (same as current-month-summary)
  // -------------------------------------------------------------------
  const sideBar = {
    toolPanels: [
      {
        id: "columns",
        labelDefault: "Columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
      },
    ],
    defaultToolPanel: "columns",
  };

  const autoGroupColumnDef = {
    minWidth: 200,
    suppressCount: false,
  };

  return (
    <div
      className="ag-table-container"
      style={{ marginLeft: open ? "240px" : "0px" }}
    >
      <Box sx={{ mt: 2 }}>
        <Card>
          <CardContent>

            {/* Filters Row */}
            <Box display="flex" gap={2} mb={3}>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                }}
              />

              <ContainedButton onClick={loadReports}>
                Load Report
              </ContainedButton>
            </Box>

            {/* REPORT TABLE */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box
                  className="ag-theme-quartz"
                  style={{ height: "calc(100vh - 220px)", width: "100%" }}
                >
                <AgGridReact
                  ref={gridRef}
                  rowData={report1}
                  columnDefs={colDefs1}
                  pivotMode={false}             // disable pivot for now
                  groupDisplayType="singleColumn"
                  autoGroupColumnDef={{
                    headerName: "Group",
                    minWidth: 200,
                    suppressCount: true,
                    cellRenderer: (params) => {
                      const label = params.node.key ?? params.value ?? "";
                      return (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: `<span style="font-weight:bold;">${label}</span>`
                          }}
                        />
                      );
                    }
                  }}
                  sideBar={sideBar}
                  animateRows={true}
                  groupDefaultExpanded={-1}
                  suppressAggFuncInHeader={true}
                  onGridReady={onGridReady}
                  onColumnVisible={onColumnVisible}
                />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </div>
  );
};

export default POReport;
